import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

interface ScriptConfig {
  url?: string;
  content?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  appendTo?: 'head' | 'body';
  id: string;
  onloadCallback?: () => {};
  // Add other relevant attributes as needed
}

@Injectable({
  providedIn: 'root'
})
export class ScriptInjectorService {

  private renderer: Renderer2;
  private injectedScripts: { [key: string]: boolean } = {};

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  injectScript(config: ScriptConfig): void {
    if (!config.url && !config.content) {
      console.warn(`Cannot inject script without URL or content:${config.id}`);
      return;
    }

    const scriptId = config.id || config.url || 'dynamic-script-' + Date.now();
    if (this.injectedScripts[scriptId]) {
      return; // Script already injected
    }

    const script = this.renderer.createElement('script');
    script.type = config.type || 'text/javascript';
    if (config.async) {
      this.renderer.setAttribute(script, 'async', 'true');
    }
    if (config.defer) {
      this.renderer.setAttribute(script, 'defer', 'true');
    }
    if (config.id) {
      this.renderer.setAttribute(script, 'id', config.id);
    }

    if (config.url) {
      this.renderer.setAttribute(script, 'src', config.url);
    } else if (config.content) {
      script.textContent = config.content;
    }

    const appendToElement = config.appendTo === 'body' ? this.document.body : this.document.head;
    this.renderer.appendChild(appendToElement, script);
    this.injectedScripts[scriptId] = true;

    // Optional: Handle script load/error events
    script.onload = () => {
      console.info(`Script loaded: ${scriptId}`);
      if(config.onloadCallback) config.onloadCallback();
    };

    script.onerror = (error:any) => {
      console.error(`Failed to load script: ${scriptId}`, error);
    };
  }
}
